import { useRef, useEffect, useState } from 'react';
import {
  Stats,
  useClearRefinements,
  useInfiniteHits,
  useSearchBox,
} from 'react-instantsearch';
import AutocompleteSearch from './AutocompleteSearch';
import { indexName, instanceSearchClient } from './SearchClient';
import EmptyResults from './EmptyResults';
import DropdownField from '../Form/DropdownField';
import RefinementListWithSearch from './RefinementListWithSearch';
import Loader from '../Loader/Loader';
import MemoSortIcon from '../../icons/SortIcon';
import { useMediaQuery } from '../../hooks';
import FilterIcon from '../../icons/FilterIcon';
import classNames from 'classnames';
import CloseIcon from '../../icons/CloseIcon';
import HubSearchResult from './HubSearchResult';
import { createInfiniteHitsSessionStorageCache } from 'instantsearch.js/es/lib/infiniteHitsCache';

interface HubSearchProps {
  suggestedSearches: string[];
  dateFilterField?: string;
  topicsTitle: string;
  setIndexForSort: (index: string) => void;
  filters: {
    topics: {
      id: string;
      title: string;
    }[];
    products: {
      id: string;
      title: string;
    }[];
  };
}

const sessionStorageCache = createInfiniteHitsSessionStorageCache();

const HubSearch: React.FC<HubSearchProps> = ({
  suggestedSearches,
  setIndexForSort,
  dateFilterField,
  topicsTitle,
}) => {
  const { refine: clear } = useClearRefinements();
  const { refine, query } = useSearchBox();
  const { href } = window.location;
  const { items, isLastPage, showMore, results, sendEvent } = useInfiniteHits({
    cache: sessionStorageCache,
  });
  const [sortValue, setSortValue] = useState(query ? 'relevance' : 'date');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const isEmpty = results.nbHits === 0;
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (results.processingTimeMS !== 0) {
      setIsLoading(false);
    }
  }, [results.processingTimeMS, searchValue, selectedTags, href]);

  useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting && !isLastPage && !isLoadingMore) {
            setIsLoadingMore(true);

            setTimeout(async () => {
              await showMore();
              setIsLoadingMore(false);
            }, 1000);
          }
        });
      });

      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [sentinelRef.current]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleRefine = (value: string, isRefined: boolean) => {
    if (isRefined) {
      setSelectedTags((prev) => [...prev, value]);
    } else {
      setSelectedTags((prev) => prev.filter((tag) => tag !== value));
    }
  };

  const SortOptions = [
    { value: 'date', label: 'Newest' },
    { value: 'relevance', label: 'Most relevant' },
  ];

  const title = window.hub_search_props?.title || '';

  // remove empty strings from suggestedSearches
  const filteredSuggestedSearches = suggestedSearches.filter(
    (suggestion) => suggestion
  );

  return (
    <div className="case-notes">
      <div className="case-notes__search">
        <div className="case-notes__form">
          <label>Search {title}</label>

          <AutocompleteSearch
            searchClient={instanceSearchClient}
            value={searchValue}
            onValueChange={setSearchValue}
          />

          {filteredSuggestedSearches &&
            filteredSuggestedSearches.length > 0 && (
              <div className="case-notes__search__suggestions">
                <ul>
                  <li>Suggested</li>

                  {filteredSuggestedSearches.map((suggestion, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsLoading(true);
                          setSearchValue(suggestion);

                          refine(suggestion);
                        }}
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      </div>

      <div className="grid--16 grid__lg--14 grid__xl-10 grid--center grid--nogutters">
        <div className="case-notes__results">
          {isMobile && (
            <button
              type="button"
              className="case-notes__results__sidebar-toggle"
              onClick={() => setFiltersModalOpen(true)}
            >
              <FilterIcon />
              <span>More Filters</span>
            </button>
          )}
          <div
            className={classNames('case-notes__results__sidebar', {
              popout: isMobile,
              'popout--open': isMobile && filtersModalOpen,
            })}
          >
            <div className="case-notes__results__sidebar-sticky">
              <button
                type="button"
                className="case-notes__results__sidebar-close"
                onClick={() => setFiltersModalOpen(false)}
              >
                <CloseIcon />
              </button>

              <RefinementListWithSearch
                attribute="Topics.objectTitle"
                hasSearchBox
                alwaysOpen={isMobile}
                title={topicsTitle}
                onRefine={handleRefine}
              />
              <RefinementListWithSearch
                attribute="Products.objectTitle"
                hasSearchBox
                alwaysOpen={isMobile}
                title="Product"
                onRefine={handleRefine}
              />

              {dateFilterField && (
                <RefinementListWithSearch
                  attribute={dateFilterField}
                  isDateFilter
                  alwaysOpen={isMobile}
                  title="Date"
                  onRefine={handleRefine}
                />
              )}

              <button
                type="button"
                className="case-notes__results__sidebar-submit button button--primary"
                onClick={() => setFiltersModalOpen(false)}
              >
                Apply filters
              </button>
            </div>
          </div>

          <div className="case-notes__results__content">
            {isLoading ? (
              <Loader />
            ) : (
              <>
                {!isEmpty && (
                  <div className="case-notes__results__header">
                    <div className="case-notes__results__count">
                      <Stats
                        translations={{
                          rootElementText({
                            nbHits,
                            nbSortedHits,
                            areHitsSorted,
                          }) {
                            return areHitsSorted && nbHits !== nbSortedHits
                              ? `${nbSortedHits!.toLocaleString()} results results`
                              : `${nbHits.toLocaleString()} results found`;
                          },
                        }}
                      />

                      {selectedTags.length > 0 && (
                        <div className="case-notes__results__filters">
                          {selectedTags.map((filter) => (
                            <button
                              key={filter}
                              type="button"
                              onClick={() => {
                                // find the matching attribute checkbox
                                const refinementInput = document.querySelector(
                                  `.case-notes__refinement-content input[type="checkbox"][name="${filter}"]`
                                ) as HTMLInputElement;

                                if (refinementInput) {
                                  // get the parent label and click it
                                  const label = refinementInput.parentElement;

                                  if (label) {
                                    label.click();
                                  }
                                }
                              }}
                            >
                              {filter} <CloseIcon />
                            </button>
                          ))}

                          <button
                            className="clear"
                            type="button"
                            onClick={() => {
                              // clear all algolia filters

                              setSelectedTags([]);
                              clear();
                            }}
                          >
                            Clear filters
                          </button>
                        </div>
                      )}
                    </div>

                    {dateFilterField && (
                      <div>
                        <div className="case-notes__results__sort form">
                          <MemoSortIcon />
                          <DropdownField
                            options={SortOptions}
                            name="sort"
                            className="case-notes__results__sort-dropdown"
                            form="case-notes"
                            defaultValue={sortValue}
                            onChange={(value) => {
                              setSortValue(value);

                              if (value === 'date') {
                                setIndexForSort(`${indexName}_newest`);
                              } else {
                                setIndexForSort(indexName);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <ul className="case-notes__results__list">
                  {isEmpty ? (
                    <EmptyResults />
                  ) : (
                    <>
                      {items.map((hit) => (
                        <HubSearchResult
                          key={hit.objectID}
                          note={hit}
                          showDate={!!dateFilterField}
                          onClick={(e) => sendEvent('click', hit)}
                        />
                      ))}

                      <li
                        ref={sentinelRef}
                        aria-hidden="true"
                        style={{ height: '20px' }}
                      >
                        <div>{isLoadingMore && <Loader />}</div>
                      </li>

                      {isLastPage && !isLoadingMore && (
                        <li className="case-notes__results__end">
                          <p>
                            We couldn&apos;t find any more results for your
                            search. If you&apos;d like to talk to us about your
                            complaint, please contact us at{' '}
                            <a href="tel:64800805950">0800 805 950</a> or{' '}
                            <a href="mailto:help@bankomb.org.nz">
                              help@bankomb.org.nz
                            </a>{' '}
                            We&apos;re here to help!
                          </p>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubSearch;
